/**
 * Documents loading bar behavior for ERP Accelerator.
 */

'use client';

import { useEffect } from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { useSearchParams } from 'next/navigation';

// Configure NProgress
NProgress.configure({
  minimum: 0.3,
  easing: 'ease',
  speed: 500,
  showSpinner: false,
});

let activeApiCount = 0;
let navInFlight = false;
let globalSafetyTimeout: ReturnType<typeof setTimeout> | undefined;

const ensureSafetyTimeout = () => {
  if (globalSafetyTimeout) clearTimeout(globalSafetyTimeout);
  globalSafetyTimeout = setTimeout(() => {
    activeApiCount = 0;
    navInFlight = false;
    NProgress.done(true);
  }, 15000);
};

const maybeStop = () => {
  if (!navInFlight && activeApiCount === 0) {
    if (globalSafetyTimeout) clearTimeout(globalSafetyTimeout);
    NProgress.done(true);
  }
};

const startNavigation = () => {
  if (!navInFlight) {
    navInFlight = true;
    NProgress.start();
    ensureSafetyTimeout();
  }
};

const stopNavigation = () => {
  navInFlight = false;
  maybeStop();
};

const startApiLoading = () => {
  activeApiCount += 1;
  if (activeApiCount === 1 && !navInFlight) {
    NProgress.start();
  }
  ensureSafetyTimeout();
};

const stopApiLoading = () => {
  activeApiCount = Math.max(0, activeApiCount - 1);
  maybeStop();
};

/**
 * Handles loading bar behavior.
 */
export default function LoadingBar() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Handle route changes
    const handleStart = () => startNavigation();
    const handleStop = () => stopNavigation();

    // Listen to route changes
    const originalPush = router.push;
    const originalReplace = router.replace;

    router.push = (...args) => {
      handleStart();
      return originalPush.apply(router, args);
    };

    router.replace = (...args) => {
      handleStart();
      return originalReplace.apply(router, args);
    };

    // Stop loading when route state changes
    handleStop();

    return () => {
      // Restore original methods
      router.push = originalPush;
      router.replace = originalReplace;
    };
  }, [pathname, searchParams, router]);

  return null;
}

// Export functions to control loading bar manually
export const startLoading = () => startApiLoading();
/**
 * Handles stop loading behavior.
 */
export const stopLoading = () => stopApiLoading();
/**
 * Handles increment loading behavior.
 */
export const incrementLoading = (amount?: number) => NProgress.inc(amount);
